// Message bubble — iMessage-style, refined for dark.
// Inbound (lead) on the left, outbound (Charles) on the right.

import { Message } from "../lib/types";
import { MascotAvatar } from "./MascotAvatar";

interface Props {
  message: Message;
}

export function ChatBubble({ message }: Props) {
  const isOutbound = message.direction === "outbound";
  const isAgentSent = message.author_type === "agent_sent";
  const isModified = message.was_modified;

  const time = new Date(message.sent_at).toLocaleString("fr-CA", {
    timeZone: "America/Montreal",
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "short",
  });

  // Voice-note placeholder: content starts with [VOICE_NOTE] and contains
  // the attachment URL. Render a friendly "🎤 message vocal — écouter
  // dans GHL" link instead of the raw marker.
  const isVoiceNote = message.content.startsWith("[VOICE_NOTE]");
  const voiceNoteUrl = isVoiceNote
    ? (message.content.match(/Attachment:\s*(\S+)/)?.[1] ?? null)
    : null;

  return (
    <div className={`flex gap-2 ${isOutbound ? "flex-row-reverse" : "flex-row"}`}>
      {!isOutbound && (
        <div className="mt-auto h-7 w-7 shrink-0 rounded-full bg-bg-elevated ring-1 ring-border" />
      )}
      {isOutbound && <MascotAvatar size={28} className="mt-auto" />}
      <div className={`flex max-w-[78%] flex-col ${isOutbound ? "items-end" : "items-start"}`}>
        <div
          className={`whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-[14.5px] leading-relaxed ${
            isOutbound
              ? "rounded-br-md bg-accent-500 text-white"
              : "rounded-bl-md bg-bg-panel text-text ring-1 ring-border"
          }`}
        >
          {isVoiceNote ? (
            <span className="italic text-text-muted">
              🎤 Message vocal du lead — pas de transcription auto.{" "}
              {voiceNoteUrl && (
                <a
                  href={voiceNoteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:text-text"
                >
                  Écouter (ouvre dans GHL)
                </a>
              )}
            </span>
          ) : (
            message.content
          )}
        </div>
        <div className="mt-1 flex items-center gap-1.5 px-1 text-[11px] text-text-dim">
          <span>{time}</span>
          {isAgentSent && (
            <>
              <span>·</span>
              <span className="text-accent-400">agent</span>
            </>
          )}
          {isModified && (
            <>
              <span>·</span>
              <span className="text-warn">✏️ édité</span>
            </>
          )}
          {message.author_type === "manual_human" && (
            <>
              <span>·</span>
              <span>manuel</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
